import { useState, useEffect } from "react";
import "./announcements.css";
import { getAllAnnouncements, approveAnnouncement, rejectAnnouncement } from "proxyUrl";
import AnnouncementTable from "./AnnouncementTable";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Announcements = () => {
  const [announcementData, setAnnouncementData] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [canApprove, setCanApprove] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    announcementType: "",
  });

  const currentUser = useSelector((state) => state.user.user);

  
  
  // Permissions
  const loggedUser = useSelector((state) => state.user.user || {});
  const userPermissions = loggedUser.userPermission || [];
  const canViewAnnouncement = userPermissions.includes("view announcement") || userPermissions.includes("view all announcement");
  const canCreateAnnouncement = userPermissions.includes("create announcement");
  const canUpdateAnnouncement = userPermissions.includes("update announcement");
  const canDeleteAnnouncement = userPermissions.includes("delete announcement");
  const canApprove = userPermissions.includes("can approve announcement")?true:false;

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.announcementType) params.append("announcementType", filters.announcementType);
      params.append("page", "1");
      params.append("limit", "100");

      const response = await axios.get(
        `${getAllAnnouncements}?${params.toString()}`,
        {
          withCredentials: true,
        }
      );

      if (response.data && response.data.data) {
        setAnnouncementData(response.data.data);
        // Check if user can approve (HR role or specific permissions)
        // This is a simple check - you may need to adjust based on your permission system
        const userRole = currentUser?.userRole?.name;
        // setCanApprove(userRole === "HR" || userRole === "Admin");
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
      toast.error("Failed to fetch announcements", {
        position: "top-center",
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleApprove = async (id) => {
    try {
      const response = await axios.post(
        `${approveAnnouncement}${id}/approve`,
        {},
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        toast.success("Announcement approved successfully!", {
          position: "top-center",
          autoClose: 2000,
        });
        fetchData(); // Refresh the list
      }
    } catch (error) {
      console.error("Error approving announcement:", error);
      toast.error(
        error.response?.data?.error || "Failed to approve announcement",
        {
          position: "top-center",
          autoClose: 2000,
        }
      );
    }
  };

  const handleReject = async (id) => {
    const rejectionReason = prompt("Please enter rejection reason:");
    if (!rejectionReason) return;

    try {
      const response = await axios.post(
        `${rejectAnnouncement}${id}/reject`,
        { rejectionReason },
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        toast.success("Announcement rejected successfully!", {
          position: "top-center",
          autoClose: 2000,
        });
        fetchData(); // Refresh the list
      }
    } catch (error) {
      console.error("Error rejecting announcement:", error);
      toast.error(
        error.response?.data?.error || "Failed to reject announcement",
        {
          position: "top-center",
          autoClose: 2000,
        }
      );
    }
  };

  if (!canViewAnnouncement) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-600">
        You are not authorized to view announcements.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="relative mx-4 mt-6 overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-indigo-600 to-indigo-700 p-4 text-white shadow-xl">
        <div className="relative z-10">
          <h1 className=" text-xl font-bold">All Announcements</h1>
          <p className="mt-2 text-sm opacity-90">
            Manage announcements, approvals, and notifications at a glance.
          </p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="mx-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-wrap items-center gap-3">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="flex-1 min-w-[150px] rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors appearance-none"
              style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="draft">Draft</option>
            </select>
            <div className="relative flex-1 min-w-[150px]">
              <select
                value={filters.announcementType}
                onChange={(e) =>
                  setFilters({ ...filters, announcementType: e.target.value })
                }
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors appearance-none"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
              >
                <option value="">All Types</option>
                <option value="General">General</option>
                <option value="Event">Event</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            <button
              onClick={() => setFilters({ status: "", announcementType: "" })}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-[#444] transition-colors hover:bg-gray-50 whitespace-nowrap"
            >
              Clear filters
            </button>
          </div>
          {canCreateAnnouncement && (
            <div className="flex-shrink-0">
              <Link to="/admin/announcements/add">
                <button className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-[1px] focus:outline-none">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Announcement
                </button>
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="mx-4">
        {loading ? (
          <div className="flex justify-center rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="text-center text-gray-500">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
              <p className="mt-4 text-sm">Loading announcements...</p>
            </div>
          </div>
        ) : announcementData && announcementData.length > 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <AnnouncementTable
              columnLenghth={announcementData.length}
              tableData={announcementData}
              editRoute={`/admin/announcements/edit`}
              deleteRoute={`/admin/announcements/delete`}
              canApprove={canApprove}
              onApprove={handleApprove}
              onReject={handleReject}
              canUpdate={canUpdateAnnouncement}
              canDelete={canDeleteAnnouncement}
            />
          </div>
        ) : (
          <div className="flex justify-center rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="mt-4 text-sm font-medium">No announcements found</p>
              <p className="mt-1 text-xs text-gray-400">Try adjusting your filters</p>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default Announcements;

